import cornerstone from 'cornerstone-core';
import {
  getPetSuvFactors,
  moToSuv,
} from './suvComputation';
import {
  buildCtVolumeGeometry,
  rasterizeRoiToCtMask,
  makeFracSampler,
  ctPixelToPatient,
} from './roiMaskGeometry';

/**
 * Includes PET voxels whose center has a trilinearly interpolated CT mask value > 0.
 */
const FRAC_THRESHOLD = 0;

const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const normalize = a => {
  const L = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / L, a[1] / L, a[2] / L];
};

/** patient point -> PET pixel index (c, r) on a given PET plane */
function patientToPetIndex(plane, P) {
  const d = sub(P, plane.ipp);
  return {
    colIndex: dot(d, plane.rowCos) / plane.colSp,
    rowIndex: dot(d, plane.colCos) / plane.rowSp,
  };
}

/** PET pixel (c, r) center -> patient point */
function petPixelToPatient(plane, colIndex, rowIndex) {
  return [
    plane.ipp[0] + colIndex * plane.colSp * plane.rowCos[0] + rowIndex * plane.rowSp * plane.colCos[0],
    plane.ipp[1] + colIndex * plane.colSp * plane.rowCos[1] + rowIndex * plane.rowSp * plane.colCos[1],
    plane.ipp[2] + colIndex * plane.colSp * plane.rowCos[2] + rowIndex * plane.rowSp * plane.colCos[2],
  ];
}

function readPetPlane(petImageId) {
  const p = cornerstone.metaData.get('imagePlaneModule', petImageId);
  if (!p || !p.imagePositionPatient || !p.rowCosines || !p.columnCosines) return null;
  return {
    id: petImageId,
    ipp: p.imagePositionPatient,
    rowCos: normalize(p.rowCosines),
    colCos: normalize(p.columnCosines),
    colSp: p.columnPixelSpacing,
    rowSp: p.rowPixelSpacing,
    rows: p.rows,
    columns: p.columns,
  };
}

/**
 * Compute the tight PET (colIndex,rowIndex) window covering the CT mask bbox on one PET slice.
 * Projects the 4 CT-bbox corners to patient, then into PET index space.
 */
function petWindowForBbox(geom, bbox, petPlane) {
  const corners = [
    [bbox.colMin, bbox.rowMin],
    [bbox.colMax, bbox.rowMin],
    [bbox.colMin, bbox.rowMax],
    [bbox.colMax, bbox.rowMax],
  ];
  let cLo = Infinity, cHi = -Infinity, rLo = Infinity, rHi = -Infinity;
  for (const [cc, rr] of corners) {
    const P = ctPixelToPatient(geom, cc, rr);
    const { colIndex, rowIndex } = patientToPetIndex(petPlane, P);
    if (colIndex < cLo) cLo = colIndex;
    if (colIndex > cHi) cHi = colIndex;
    if (rowIndex < rLo) rLo = rowIndex;
    if (rowIndex > rHi) rHi = rowIndex;
  }
  return {
    cLo: Math.max(0, Math.floor(cLo) - 1),
    cHi: Math.min(petPlane.columns - 1, Math.ceil(cHi) + 1),
    rLo: Math.max(0, Math.floor(rLo) - 1),
    rHi: Math.min(petPlane.rows - 1, Math.ceil(rHi) + 1),
  };
}

export async function samplePetVoxelsForRoi({
  contoursByCtSlice,
  ctImageIds,
  petImageIds,
}) {
  const geom = buildCtVolumeGeometry(ctImageIds);
  if (!geom) return { ok: false, reason: 'bad-ct-geometry' };

  const rasterized = rasterizeRoiToCtMask(contoursByCtSlice, geom);
  if (!rasterized) return { 
    ok: true, 
    suv: new Float32Array(0), 
    frac: new Float32Array(0), 
    voxelIndex: new Int32Array(0) 
  };
  const fracAt = makeFracSampler(rasterized, geom);

  const factors = getPetSuvFactors(petImageIds[0]);
  if (!factors.ok) return { ok: false, reason: `factors:${factors.reason}` };

  // ROI z-range along the CT normal, padded by one CT slice
  const projMin = geom.proj0 + (rasterized.sliceMin - 1) * geom.dz;
  const projMax = geom.proj0 + (rasterized.sliceMax + 1) * geom.dz;

  const suvOut = [];
  const fracOut = [];
  const idxOut = [];
    

  for (let petArrayIdx = 0; petArrayIdx < petImageIds.length; petArrayIdx++) {
    const petPlane = readPetPlane(petImageIds[petArrayIdx]);
    if (!petPlane) continue;

    const proj = dot(petPlane.ipp, geom.normal);
    if (proj < projMin || proj > projMax) continue; // outside ROI z-extent

    const win = petWindowForBbox(geom, rasterized.bbox, petPlane);
    if (win.cHi < win.cLo || win.rHi < win.rLo) continue;

    const image = await cornerstone.loadAndCacheImage(petPlane.id);
    const pd = image.getPixelData();
    const slope = image.slope != null ? image.slope : 1;
    const intercept = image.intercept != null ? image.intercept : 0;
    const petCols = petPlane.columns;
    const sliceOffset = petArrayIdx * petPlane.rows * petCols;


    for (let rowIndex = win.rLo; rowIndex <= win.rHi; rowIndex++) {
      for (let colIndex = win.cLo; colIndex <= win.cHi; colIndex++) {
        const P = petPixelToPatient(petPlane, colIndex, rowIndex);
        const frac = fracAt(P);
        if (frac <= FRAC_THRESHOLD) continue;

        const raw = pd[rowIndex * petCols + colIndex];
        const mo = raw * slope + intercept;
        const suv = moToSuv(mo, factors.decayCorrectedDoseBq, factors.patientWeightKg);
        if (suv == null) continue; // invalid SUV -> treat as no coverage.
        suvOut.push(suv);
        fracOut.push(frac);
        idxOut.push(sliceOffset + rowIndex * petCols + colIndex);
      }
    }
  }

  return {
    ok: true,
    suv: Float32Array.from(suvOut),
    frac: Float32Array.from(fracOut),
    voxelIndex: Int32Array.from(idxOut),
  };
}